import { useState } from 'react';
import { supabase } from '@/utils/supabase-client';
import Head from 'next/head';
import Link from 'next/link';
import { format } from 'date-fns';
import { SEOMeta } from '@/templates/SEOMeta';


export default function BlogPage({ initialPosts }) {
    const [posts, setPosts] = useState(initialPosts);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState('all');
    const siteUrl = 'https://afivio.com';
    const canonicalUrl = `${siteUrl}/blog`;
    const pageTitle = 'Affiliate Marketing Blog | Afivio';
    const pageDescription = 'Practical guides on affiliate marketing, referral programs, partner growth, and campaign analytics.';

    // Фільтрація постів
    const filteredPosts = posts
        .filter(post => {
            // Фільтр по категорії
            if (category !== 'all') {
                const postCats = Array.isArray(post.category)
                    ? post.category
                    : (typeof post.category === 'string' && post.category.trim().startsWith('[')
                        ? (() => {
                            try { return JSON.parse(post.category); } catch (e) { return [post.category]; }
                        })()
                        : (post.category != null ? [post.category] : []));

                if (!postCats.some(pc => pc === category)) return false;
            }
            // Фільтр по пошуку
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                return (
                    post.title.toLowerCase().includes(searchLower) ||
                    (post.excerpt && post.excerpt.toLowerCase().includes(searchLower)) ||
                    (post.tags && post.tags.toLowerCase().includes(searchLower))
                );
            }
            return true;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Категорії для фільтрації
    const categories = [
        { id: 'all', name: 'All articles' },
        { id: 'marketing', name: 'Marketing' },
        { id: 'business', name: 'Business' },
        { id: 'technology', name: 'Technology' },
        { id: 'analytics', name: 'Analytics' },
        { id: 'research', name: 'Research and cases' },
    ];

    const schemaPosts = (initialPosts || [])
        .filter((post) => post?.slug && post.is_public !== false)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 12);

    const blogStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#itemlist`,
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
        numberOfItems: schemaPosts.length,
        itemListElement: schemaPosts.map((post, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${siteUrl}/blog/${post.slug}`,
            item: {
                '@type': 'BlogPosting',
                headline: post.title,
                datePublished: post.created_at || undefined
            }
        }))
    };

    return (
        <div className="bg-gray-50 min-h-screen">
            <SEOMeta
                title={pageTitle}
                description={pageDescription}
                keywords="affiliate marketing blog, referral marketing guides, partner program tips, creator partnerships, campaign analytics"
                url="/blog"
            />
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(blogStructuredData) }}
                />
            </Head>

            {/* Герой секція */}
            <section className="bg-gradient-to-r from-blue-600 to-green-500 text-white py-16 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">Afivio Blog</h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        Learn about the latest trends in marketing, business strategies and technologies
                    </p>
                </div>
            </section>

            {/* Фільтри та пошук */}
            <section className="py-8 bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-center items-center gap-4">
                        <div className="flex flex-wrap gap-2 justify-center">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setCategory(cat.id)}
                                    className={`px-4 py-2 rounded-full text-sm ${
                                        category === cat.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>


            {/* Список статей */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4">
                    {filteredPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post) => (
                                <article
                                    key={post.id}
                                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                                >
                                    <Link href={`/blog/${post.slug}`}>
                                        <div>
                                            {/* Зображення статті */}
                                            <div className="aspect-video bg-gray-200 border-2 border-dashed w-full">
                                                {post.image ? (
                                                    <img
                                                        src={post.image}
                                                        alt={post.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-gray-400">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-12 w-12"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Контент статті  */}
                                            <div className="p-6">
                                                <h2 className="text-xl font-bold mb-3 hover:text-blue-600 transition-colors">
                                                    {post.title}
                                                </h2>

                                                <p className="text-gray-600 mb-4 line-clamp-3">
                                                    {post.excerpt || 'There is no description for this article...'}
                                                </p>

                                                <div className="flex flex-wrap gap-2 mb-4">
                                                    {post.tags &&
                                                        post.tags
                                                            .split(',')
                                                            .slice(0, 3)
                                                            .map((tag, index) => (
                                                                <span
                                                                    key={index}
                                                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                                                >
                                                                    {tag.trim()}
                                                                </span>
                                                            ))}
                                                </div>

                                                <div className="flex items-center text-sm text-gray-500 mb-3">
                                                    <span>
                                                        {format(new Date(post.created_at), 'MMMM d, yyyy')}
                                                    </span>
                                                    <span className="mx-2">•</span>
                                                    <span>{post.reading_time || '5'} min read</span>
                                                    {!post.is_public && (
                                                        <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                                            Customers only
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-16 w-16 mx-auto text-gray-400 mb-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h3 className="text-xl font-medium text-gray-700 mb-2">No articles found</h3>
                            <p className="text-gray-500">
                                Try changing your search terms or selecting a different category
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export async function getServerSideProps() {
    let posts = [];
    try {
        const { data, error } = await supabase
            .from('posts')
            .select('*')
            .order('created_at', { ascending: false });

        if (!error) posts = data;
    } catch (e) {
        console.error("Error fetching posts:", e);
    }

    return { props: { initialPosts: posts } };
}